<template>
    <div class="ps-page-body">
        <SeoHead
            :title="title || 'FAQ'"
            description="Answers about ProfileSuite print cards, digital tools, self-hosting, and getting started."
        />

        <marketing-page-hero
            eyebrow="Help center"
            :title="__('Frequently Asked Questions')"
            :lead="__('Quick answers about Print Studio, digital tools, deployment, and day-to-day use.')"
        >
            <template #actions>
                <form class="ps-page-hero__search" role="search" @submit.prevent>
                    <svg class="ps-page-hero__search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        id="faq-search"
                        v-model="form.search"
                        type="search"
                        class="ps-page-hero__search-input"
                        :placeholder="__('Search your question…')"
                        autocomplete="off"
                    >
                </form>
            </template>
        </marketing-page-hero>

        <section class="ps-page-body__section">
            <div class="container max-w-3xl">
                <div id="accordion-open" data-accordion="open">
                    <div v-for="(faq, fi) in faqs.data" :key="faq.id">
                        <h2 :id="`accordion-heading-${fi}`">
                            <button
                                type="button"
                                class="flex items-center justify-between w-full p-5 font-medium text-left text-gray-500 border border-gray-200 focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-800 dark:border-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                                :class="{'rounded-t-xl' : fi===0, 'border-b-0' : fi!==(faqs.data.length - 1)}"
                                :aria-expanded="faq.active ? 'true' : 'false'"
                                :aria-controls="`accordion-body-${fi}`"
                                @click="toggleActive(fi)"
                            >
                                <span class="flex items-center">
                                    <svg class="w-5 h-5 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" /></svg>
                                    {{ faq.name }}
                                </span>
                                <svg class="w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" :class="{'rotate-180' : faq.active}"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                            </button>
                        </h2>
                        <div
                            :id="`accordion-body-${fi}`"
                            :class="{'hidden' : !faq.active}"
                            :aria-labelledby="`accordion-heading-${fi}`"
                        >
                            <div class="p-5 border border-gray-200 dark:border-gray-700 dark:bg-gray-900" :class="{'border-b-0' : fi!==(faqs.data.length - 1), 'border-t-0' : fi===(faqs.data.length - 1)}">
                                <div class="text-gray-500 dark:text-gray-400 prose prose-sm max-w-none" v-html="sanitizeHtml(faq.details)" />
                            </div>
                        </div>
                    </div>
                </div>

                <p v-if="!faqs.data.length" class="text-center text-slate-500 py-12">
                    {{ __('No matching questions found. Try a different search term.') }}
                </p>
            </div>
        </section>
    </div>
</template>

<script>
import Layout from '@/Shared/Landing/Layout'
import MarketingPageHero from '@/Shared/Landing/MarketingPageHero'
import SeoHead from '@/Shared/SeoHead'
import sanitizeHtml from 'sanitize-html'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'

export default {
    layout: Layout,
    components: {
        MarketingPageHero,
        SeoHead,
    },
    props: {
        faqs: Object,
        title: String,
        filters: Object,
    },
    data() {
        return {
            form: {
                search: this.filters?.search || '',
            },
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('faq'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        toggleActive(index) {
            this.faqs.data[index].active = !this.faqs.data[index].active
        },
        sanitizeHtml,
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
    created() {
        if (this.faqs.data.length) {
            this.faqs.data[0].active = true
        }
    },
}
</script>
